// src/utils/passwordUtils.js

/**
 * Password validation utilities for financial SaaS platform
 * Implements industry-standard security requirements
 */

// Common weak passwords list (top 100 for demo, expand to 10,000 in production)
const COMMON_PASSWORDS = [
  'password', 'Password', 'password123', 'Password123', '12345678', '123456789',
  'qwerty', 'abc123', 'monkey', '1234567890', 'letmein', 'trustno1',
  'dragon', 'baseball', 'iloveyou', 'master', 'sunshine', 'ashley',
  'bailey', 'shadow', 'superman', 'qazwsx', 'michael', 'football'
];

/**
 * Password strength levels
 */
export const STRENGTH_LEVELS = {
  WEAK: { level: 0, label: 'Weak', color: 'red', gradient: 'from-red-500 to-red-600' },
  FAIR: { level: 1, label: 'Fair', color: 'orange', gradient: 'from-orange-500 to-yellow-500' },
  GOOD: { level: 2, label: 'Good', color: 'yellow', gradient: 'from-yellow-500 to-green-400' },
  STRONG: { level: 3, label: 'Strong', color: 'lightgreen', gradient: 'from-green-400 to-green-500' },
  EXCELLENT: { level: 4, label: 'Excellent', color: 'green', gradient: 'from-green-500 to-emerald-600' }
};

/**
 * Password requirements for financial platform
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
  preventCommon: true,
  preventRepeating: true,
  maxRepeatingChars: 3
};

/**
 * Check if password meets minimum requirements
 */
export const validatePasswordRequirements = (password: string) => {
  const requirements = {
    length: {
      met: password.length >= PASSWORD_REQUIREMENTS.minLength,
      message: `At least ${PASSWORD_REQUIREMENTS.minLength} characters`
    },
    uppercase: {
      met: /[A-Z]/.test(password),
      message: 'One uppercase letter'
    },
    lowercase: {
      met: /[a-z]/.test(password),
      message: 'One lowercase letter'
    },
    number: {
      met: /\d/.test(password),
      message: 'One number'
    },
    special: {
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      message: 'One special character'
    }
  };

  const allMet = Object.values(requirements).every(req => req.met);
  
  return {
    requirements,
    allMet,
    score: Object.values(requirements).filter(req => req.met).length
  };
};

/**
 * Check for common password patterns
 */
export const hasCommonPatterns = (password: string) => {
  const lowerPassword = password.toLowerCase();
  
  // Check against common passwords
  if (COMMON_PASSWORDS.includes(lowerPassword)) {
    return { found: true, reason: 'Common password detected' };
  }
  
  // Check for sequential characters
  const sequences = ['abcd', 'bcde', 'cdef', 'defg', '1234', '2345', '3456', 'qwer', 'asdf', 'zxcv'];
  for (const seq of sequences) {
    if (lowerPassword.includes(seq)) {
      return { found: true, reason: 'Sequential characters detected' };
    }
  }
  
  // Check for repeated characters (e.g., 'aaa', '111')
  const repeatedPattern = /(.)\1{3,}/;
  if (repeatedPattern.test(password)) {
    return { found: true, reason: 'Too many repeated characters' };
  }
  
  // Check for keyboard patterns
  const keyboardPatterns = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', 'poiuytrewq', 'lkjhgfdsa', 'mnbvcxz'];
  for (const pattern of keyboardPatterns) {
    if (lowerPassword.includes(pattern.substring(0, 4))) {
      return { found: true, reason: 'Keyboard pattern detected' };
    }
  }
  
  return { found: false };
};

/**
 * Calculate password strength with detailed scoring
 */
export const calculatePasswordStrength = (password: string) => {
  if (!password) {
    return {
      strength: STRENGTH_LEVELS.WEAK,
      score: 0,
      feedback: [],
      percentage: 0
    };
  }

  let score = 0;
  const feedback = [];
  
  // Length scoring (0-30 points)
  const lengthScore = Math.min(password.length * 2, 30);
  score += lengthScore;
  
  // Complexity scoring (0-40 points)
  const validation = validatePasswordRequirements(password);
  score += validation.score * 8;
  
  // Diversity scoring (0-20 points)
  const uniqueChars = new Set(password).size;
  const diversityRatio = uniqueChars / password.length;
  score += Math.floor(diversityRatio * 20);
  
  // Pattern penalty (-30 points)
  const patterns = hasCommonPatterns(password);
  if (patterns.found) {
    score -= 30;
    feedback.push(patterns.reason);
  }
  
  // Bonus for extra length (0-10 points)
  if (password.length >= 16) {
    score += 10;
    feedback.push('Excellent length');
  }
  
  // Ensure score is between 0 and 100
  score = Math.max(0, Math.min(100, score));
  
  // Determine strength level
  let strength;
  if (score < 20) strength = STRENGTH_LEVELS.WEAK;
  else if (score < 40) strength = STRENGTH_LEVELS.FAIR;
  else if (score < 60) strength = STRENGTH_LEVELS.GOOD;
  else if (score < 80) strength = STRENGTH_LEVELS.STRONG;
  else strength = STRENGTH_LEVELS.EXCELLENT;
  
  // Add requirement-based feedback
  if (!validation.allMet) {
    Object.entries(validation.requirements).forEach(([key, req]) => {
      if (!req.met) {
        feedback.push(`Missing: ${req.message}`);
      }
    });
  }
  
  return {
    strength,
    score,
    feedback,
    percentage: score,
    meetsRequirements: validation.allMet && !patterns.found
  };
};

/**
 * Generate a secure random password using the Web Crypto API
 */
export const generateSecurePassword = (length: number = 16) => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  const allChars = uppercase + lowercase + numbers + special;
  
    const getSecureRandomIndex = (charSet: string | { length: number }) => {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  const maxIndex = typeof charSet === 'string' ? charSet.length : charSet.length;
  return randomBuffer[0] % maxIndex;

};

  const passwordArr = [];
  
  // Ensure at least one of each required type, securely
  passwordArr.push(uppercase[getSecureRandomIndex(uppercase)]);
  passwordArr.push(lowercase[getSecureRandomIndex(lowercase)]);
  passwordArr.push(numbers[getSecureRandomIndex(numbers)]);
  passwordArr.push(special[getSecureRandomIndex(special)]);
  
  // Fill the rest of the password length with secure random characters
  for (let i = passwordArr.length; i < length; i++) {
    passwordArr.push(allChars[getSecureRandomIndex(allChars)]);
  }
  
  // Securely shuffle the password array using the Fisher-Yates algorithm
  for (let i = passwordArr.length - 1; i > 0; i--) {
    const j = getSecureRandomIndex({ length: i + 1 }); // Get a random index from 0 to i
    // Swap elements
    [passwordArr[i], passwordArr[j]] = [passwordArr[j], passwordArr[i]];
  }
  
  return passwordArr.join('');
};

/**
 * Hash password for history comparison (using Web Crypto API)
 */
export const hashPasswordForHistory = async (password: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Check if password was recently used
 */
export const checkPasswordHistory = async (password: string, historyHashes: string[] = []) => {
  if (historyHashes.length === 0) return false;
  const currentHash = await hashPasswordForHistory(password);
  return historyHashes.includes(currentHash); // Remove the type cast
};

export default {
  validatePasswordRequirements,
  calculatePasswordStrength,
  generateSecurePassword,
  hashPasswordForHistory,
  checkPasswordHistory,
  hasCommonPatterns,
  STRENGTH_LEVELS,
  PASSWORD_REQUIREMENTS
};